import { IS_GATEWAY_V2 } from "@/contract/gateway-version";

/**
 * `mint` changed arity between market generations, mirroring buy-nft.ts.
 *
 * V1: mint(uint256[] tokenIds, address parent)
 * V2: mint(uint256[] tokenIds, address referrer, address parent)
 *
 * Gateway V2 splits placement into PlacementInput{referrer, parent} -- the
 * referrer earns the referral cut, the parent is the binary-tree position.
 * The selectors differ, so calling the V2 shape against a deployed V1 market
 * does not mis-execute -- it reverts.
 */

const SHARED = [
  {
    inputs: [{ internalType: "address", name: "owner", type: "address" }],
    name: "OwnableInvalidOwner",
    type: "error",
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "OwnableUnauthorizedAccount",
    type: "error",
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address",
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address",
      },
    ],
    name: "OwnershipTransferred",
    type: "event",
  },
  {
    inputs: [],
    name: "GATEWAY",
    outputs: [{ internalType: "contract Gateway", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "MAX_ID",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "NFT_LAND",
    outputs: [{ internalType: "contract NFTLand", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "SELLER",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "max_id", type: "uint256" }],
    name: "changeMaxId",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "land_id", type: "uint256" }],
    name: "getMintPrice",
    outputs: [
      { internalType: "uint256", name: "bv", type: "uint256" },
      { internalType: "uint256", name: "sv", type: "uint256" },
    ],
    stateMutability: "pure",
    type: "function",
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "sellerToken",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint8", name: "token_id", type: "uint8" }],
    name: "setToken",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

export const LAND_MARKET_ABI_V1 = [
  {
    inputs: [
      { internalType: "address", name: "nft_land_address", type: "address" },
      { internalType: "address", name: "gateway_address", type: "address" },
      { internalType: "address", name: "seller", type: "address" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
  ...SHARED,
  {
    inputs: [
      { internalType: "uint256[]", name: "_tokenIds", type: "uint256[]" },
      { internalType: "address", name: "parent", type: "address" },
    ],
    name: "mint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

export const LAND_MARKET_ABI_V2 = [
  {
    inputs: [
      { internalType: "address", name: "nft_land_address", type: "address" },
      { internalType: "address", name: "gateway_address", type: "address" },
      { internalType: "address", name: "seller", type: "address" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
  ...SHARED,
  {
    inputs: [
      { internalType: "uint256[]", name: "_tokenIds", type: "uint256[]" },
      { internalType: "address", name: "referrer", type: "address" },
      { internalType: "address", name: "parent", type: "address" },
    ],
    name: "mint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

export const LAND_MARKET_ABI = (
  IS_GATEWAY_V2 ? LAND_MARKET_ABI_V2 : LAND_MARKET_ABI_V1
) as typeof LAND_MARKET_ABI_V2;

/**
 * Mint arguments in the shape the configured land market expects. Returned as
 * a plain array so the caller appends `tokenIds` first, then spreads this.
 */
export const landMarketMintArgs = (
  parentAddress: `0x${string}`,
  referrerAddress: `0x${string}`,
): `0x${string}`[] =>
  IS_GATEWAY_V2 ? [referrerAddress, parentAddress] : [parentAddress];
